import { Typography } from '@remarkable/ark-web';
import toast from 'react-hot-toast';
import type { EnterpriseDomain } from 'src/api/endpoints/cloudEnterpriseAdminApi.types';
import { useEnableEnterpriseDomainForwarding } from 'src/api/queries/enterpriseAdmin';
import { TestId } from 'src/testids';
import { Modal } from '.';
import { Button } from './Button';
import { NotificationBox } from './NotificationBox';

export const StartEnterpriseDomainForwardingNotificationModal = ({
  setIsOpen,
  primaryDomain,
  componentLocation,
}: {
  primaryDomain?: EnterpriseDomain;
  componentLocation: string;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const enableDomainForwarding = useEnableEnterpriseDomainForwarding();
  return (
    <Modal.Content
      data-cy={TestId.ModalEnterpriseStartDomainForwarding}
      className="flex max-w-3xl flex-col gap-24"
    >
      <Typography variant="heading-lg" as="h2">
        Are you sure you want to continue?
      </Typography>
      <Typography variant="body-md">
        Activating domain forwarding will force all users from the domain to
        authenticate via SSO. At this point, login via legacy credentials is no
        longer supported.
      </Typography>

      <NotificationBox
        variant="warning"
        title="Make sure users are added to the IdP before activating domain forwarding"
      >
        If you have not added users to your IdP, they will not be able to access
        the application, as they will be unable to authenticate through SSO.
      </NotificationBox>

      <div className="flex justify-center gap-24">
        <Modal.Close asChild>
          <Button variant="secondary">Cancel</Button>
        </Modal.Close>
        <Button
          variant="primary"
          disabled={enableDomainForwarding.isPending}
          loading={enableDomainForwarding.isPending}
          analytics={{
            action: 'start enterprise domain forwarding',
            location: componentLocation,
            text: 'Continue',
          }}
          onClick={() => {
            enableDomainForwarding.mutate(
              { domain: primaryDomain?.address },
              {
                onSuccess: () => {
                  toast.success('Migration completed');
                  setIsOpen(false);
                },
              }
            );
          }}
        >
          Continue
        </Button>
      </div>

      {enableDomainForwarding.isError && (
        <NotificationBox
          error={enableDomainForwarding.error}
          title="Something went wrong"
        >
          {enableDomainForwarding.error.message ||
            'Please try again later or contact support if the problem persists.'}
        </NotificationBox>
      )}
    </Modal.Content>
  );
};
